import { DOCUMENT } from "@angular/common";
import { Injectable, inject } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Observable, of } from "rxjs";
import { catchError, map, take } from "rxjs/operators";

import {
  CountryCodeEnum,
  LanguageEnum,
} from "src/app/shared/models/country.model";
import {
  LANDING_ASSET_BASE_PATH,
  LANDING_HEADER_ANIMATION_PATH_MAP,
  LANDING_CALENDAR_IMAGE_MAP,
  LANDING_CORPORATE_LINK_CONFIG,
  LANDING_COUNTRY_LANGUAGE_MAP,
  LANDING_DEFAULT_COUNTRY,
  LANDING_FACEBOOK_LINK_MAP,
  LANDING_FLOATING_BUTTON_UI_CONFIG_MAP,
  LANDING_HEADER_COUNTRY_OVERRIDE_MAP,
  LANDING_HEADER_MOBILE_BACKGROUND_PATH,
  LANDING_HEADER_TOP_GRAPHIC_PATH,
  LANDING_HTML_LANG_MAP,
  LANDING_LOW_INTEREST_ICON_MAP,
  LANDING_LOW_INTEREST_IMAGE_MAP,
  LANDING_UTM_KEYS,
  LandingCorporateLinkConfig,
  LandingFloatingButtonConfig,
  LandingHeaderConfig,
  LandingHeaderCountryOverride,
  LandingHeaderMediaType,
  SUPPORTED_COUNTRIES,
} from "src/app/shared/models/landing.model";
import { GTM_EVENT_NAMES } from "src/app/shared/consts";
import { AnalyticsService } from "src/app/shared/services/analytics.service";
import { RestoreLandingGa4EventsExperimentKeyService } from "src/app/experiments/restore-landing-ga4-events/restore-landing-ga4-events.service";
import { MIXPANEL_EVENTS } from "src/app/shared/models/mixpanel.model";
import { MixpanelService } from "src/app/shared/services/mixpanel.service";
import { environment } from "src/environments/environment";

export interface LandingAnalyticsContext {
  landingType?: "default" | "campaign";
  campaignSlug?: string;
}

const LANDING_PAGE_VERSION = "v2" as const;

type LandingGtmEvent =
  | { event: GTM_EVENT_NAMES.countryCodeReady }
  | { event: GTM_EVENT_NAMES.landingPageOpened }
  | {
      event: GTM_EVENT_NAMES.landingPageButtonClicked;
      landing_page_version: typeof LANDING_PAGE_VERSION;
    };

/**
 * Provides landing-country utilities for validation, language mapping, and
 * country-specific configuration such as assets, legal links, and social URLs.
 * Also exposes landing analytics helpers and UTM registration utilities.
 */
@Injectable({ providedIn: "root" })
export class LandingService {
  private readonly document = inject<Document>(DOCUMENT);
  private readonly mixpanel = inject(MixpanelService);
  private readonly translate = inject(TranslateService);
  private readonly analytics = inject(AnalyticsService);
  private readonly restoreGa4Events = inject(
    RestoreLandingGa4EventsExperimentKeyService
  );

  /**
   * Checks whether the input can be normalized to a supported landing country.
   *
   * @param country Candidate country code from route or storage.
   * @returns `true` when the value maps to a supported country.
   */
  isValidCountry(country: string | null | undefined): boolean {
    return !!this.normalizeCountryCode(country);
  }

  /**
   * Normalizes and validates a country code, falling back to Thailand when invalid.
   *
   * @param countryParam Raw country value from URL or external input.
   * @returns Supported country code.
   */
  resolveCountryWithFallback(
    countryParam: string | null | undefined
  ): CountryCodeEnum {
    return this.normalizeCountryCode(countryParam) ?? LANDING_DEFAULT_COUNTRY;
  }

  /**
   * Returns the language file enum for the provided country.
   *
   * @param country Supported country code.
   * @returns Language enum used by TranslateService.
   */
  getLanguageForCountry(country: CountryCodeEnum): LanguageEnum {
    return LANDING_COUNTRY_LANGUAGE_MAP[country];
  }

  /**
   * Loads the landing translation bundle and synchronizes the document language.
   *
   * @param country Supported country code.
   * @returns Observable emitting `true` when preload completes or falls back.
   */
  preloadTranslationsForCountry(country: CountryCodeEnum): Observable<boolean> {
    const language = this.getLanguageForCountry(country);
    return this.translate.use(language).pipe(
      take(1),
      map(() => {
        this.document.documentElement.lang =
          LANDING_HTML_LANG_MAP[language] ?? LANDING_HTML_LANG_MAP.en;
        return true;
      }),
      catchError(() => {
        this.document.documentElement.lang = LANDING_HTML_LANG_MAP.en;
        return of(true);
      })
    );
  }

  /**
   * Returns the country-specific Facebook profile used on landing pages.
   *
   * @param country Supported country code.
   * @returns Facebook profile URL.
   */
  getFacebookLink(country: CountryCodeEnum): string {
    return LANDING_FACEBOOK_LINK_MAP[country];
  }

  /**
   * Returns chat floating action button config for the selected country.
   *
   * @param country Supported country code.
   * @returns FAB link, icon, and visual variant for the country.
   */
  getFloatingButtonConfig(
    country: CountryCodeEnum
  ): LandingFloatingButtonConfig {
    return LANDING_FLOATING_BUTTON_UI_CONFIG_MAP[country];
  }

  /**
   * Resolves landing header configuration for a country.
   * The resolver starts from default lottie behavior and merges optional
   * country overrides (e.g. PH image header) and future experiment overrides.
   *
   * @param country Supported country code.
   * @returns Header config consumed by LandingHeaderComponent.
   */
  getHeaderConfig(country: CountryCodeEnum): LandingHeaderConfig {
    const defaultConfig: LandingHeaderConfig =
      this.createDefaultHeaderConfig(country);
    const countryOverride: LandingHeaderCountryOverride | undefined =
      LANDING_HEADER_COUNTRY_OVERRIDE_MAP[country];

    return this.applyHeaderExperimentOverrides(country, {
      ...defaultConfig,
      ...countryOverride,
    });
  }

  /**
   * Returns the calendar image asset path for the selected country.
   *
   * @param country Supported country code.
   * @returns Public asset path.
   */
  getCalendarImg(country: CountryCodeEnum): string {
    return LANDING_CALENDAR_IMAGE_MAP[country];
  }

  /**
   * Returns the low-interest mascot image asset path for the selected country.
   *
   * @param country Supported country code.
   * @returns Public asset path.
   */
  getLowInterestMascotImage(country: CountryCodeEnum): string {
    return LANDING_LOW_INTEREST_IMAGE_MAP[country];
  }

  /**
   * Returns the low-interest currency icon asset path for the selected country.
   *
   * @param country Supported country code.
   * @returns Public asset path.
   */
  getLowInterestCurrencyIcon(country: CountryCodeEnum): string {
    return LANDING_LOW_INTEREST_ICON_MAP[country];
  }

  /**
   * Returns the currency icon asset path for the selected country.
   *
   * @param country Supported country code.
   * @returns Public asset path.
   */
  getCurrencyIcon(country: CountryCodeEnum): string {
    return `${LANDING_ASSET_BASE_PATH}/images/icon-money-${country.toLowerCase()}.png`;
  }

  /**
   * Returns the header animation asset path for the selected country.
   *
   * @param country Supported country code.
   * @returns Public asset path.
   */
  getHeaderAnimation(country: CountryCodeEnum): string {
    return LANDING_HEADER_ANIMATION_PATH_MAP[country];
  }

  /**
   * Builds country-aware corporate/legal page links.
   *
   * @param link Relative legal page slug (without `.html`).
   * @param country Supported country code.
   * @returns Fully qualified URL for legal pages.
   */
  getCorporateLinkLocale(link: string, country: CountryCodeEnum): string {
    const config: LandingCorporateLinkConfig =
      LANDING_CORPORATE_LINK_CONFIG[country];
    const normalizedLink = this.normalizeLegalLink(link);
    const url = new URL(`${normalizedLink}.html`, config.domain);

    if (config.countryParam) {
      url.searchParams.set("country", config.countryParam);
    }

    if (config.localeParam) {
      url.searchParams.set("locale", config.localeParam);
    }

    return url.toString();
  }

  /**
   * Tracks landing page view for the provided country.
   *
   * @param country Supported country code.
   */
  trackPageView(
    country: CountryCodeEnum,
    context: LandingAnalyticsContext = {}
  ): void {
    this.mixpanel.track(MIXPANEL_EVENTS.LANDING_PAGE_VIEWED, {
      page_name: "landing",
      country,
      ...this.buildCampaignAnalyticsContext(context),
    });

    this.restoreGa4Events
      .whenReady$()
      .pipe(take(1))
      .subscribe((enabled) => {
        if (!enabled) {
          return;
        }

        window.gtm_country_code = country;

        this.pushLandingGtmEvent({ event: GTM_EVENT_NAMES.countryCodeReady });
        this.pushLandingGtmEvent({ event: GTM_EVENT_NAMES.landingPageOpened });
      });
  }

  /**
   * Tracks CTA click interactions on landing pages.
   *
   * @param country Supported country code.
   * @param ctaType Identifier for clicked CTA.
   */
  trackCtaClick(
    country: CountryCodeEnum,
    ctaType: string,
    context: LandingAnalyticsContext = {}
  ): void {
    this.mixpanel.track(MIXPANEL_EVENTS.LANDING_CTA_CLICK, {
      country,
      cta_type: ctaType,
      ...this.buildCampaignAnalyticsContext(context),
    });

    this.restoreGa4Events
      .whenReady$()
      .pipe(take(1))
      .subscribe((enabled) => {
        if (!enabled) {
          return;
        }

        this.pushLandingGtmEvent({
          event: GTM_EVENT_NAMES.landingPageButtonClicked,
          landing_page_version: LANDING_PAGE_VERSION,
        });
      });
  }

  /**
   * Tracks section view interactions on landing pages.
   *
   * @param country Supported country code.
   * @param section Identifier for viewed section.
   */
  trackSectionView(
    country: CountryCodeEnum,
    section: string,
    context: LandingAnalyticsContext = {}
  ): void {
    this.mixpanel.track(MIXPANEL_EVENTS.LANDING_SECTION_VIEW, {
      country,
      section,
      ...this.buildCampaignAnalyticsContext(context),
    });
  }

  /**
   * Reads UTM parameters from the current URL and registers them as Mixpanel properties.
   *
   * @param search Optional query string override for tests.
   */
  captureAndRegisterUtms(
    search: string = globalThis.location?.search ?? ""
  ): void {
    const utms = this.extractUtmsFromSearch(search);
    if (Object.keys(utms).length > 0) {
      this.mixpanel.registerUtms(utms);
    }
  }

  /**
   * Builds the signup/onboarding URL while preserving current landing query params.
   * This keeps UTMs and campaign params when users move from landing to onboarding.
   *
   * @param search Query string from current landing URL.
   * @returns Absolute signup URL with preserved query parameters.
   */
  getSignupHref(search: string = globalThis.location?.search ?? ""): string {
    return this.buildPreservedLandingUrl("/", search);
  }

  private buildPreservedLandingUrl(
    pathname: string,
    search: string = globalThis.location?.search ?? ""
  ): string {
    const signupUrl = new URL(environment.webLink);
    const currentParams = new URLSearchParams(search);

    signupUrl.pathname = pathname;

    currentParams.forEach((value, key) => {
      signupUrl.searchParams.set(key, value);
    });

    return signupUrl.toString();
  }

  /**
   * Normalizes country code casing and validates against supported countries.
   *
   * @param country Raw country input.
   * @returns Supported country code or `null`.
   */
  private normalizeCountryCode(
    country: string | null | undefined
  ): CountryCodeEnum | null {
    if (!country) {
      return null;
    }

    const normalizedCountry = country.toUpperCase();
    return SUPPORTED_COUNTRIES.includes(normalizedCountry as CountryCodeEnum)
      ? (normalizedCountry as CountryCodeEnum)
      : null;
  }

  /**
   * Strips optional `.html` suffix from legal link slugs.
   *
   * @param link Raw legal link value.
   * @returns Normalized slug without extension.
   */
  private normalizeLegalLink(link: string): string {
    return link.endsWith(".html") ? link.replace(/\.html$/, "") : link;
  }

  private buildCampaignAnalyticsContext(
    context: LandingAnalyticsContext
  ): Record<string, string> {
    if (context.landingType !== "campaign" || !context.campaignSlug) {
      return {};
    }

    return {
      landing_type: "campaign",
      campaign_slug: context.campaignSlug,
    };
  }

  private pushLandingGtmEvent(payload: LandingGtmEvent): void {
    this.analytics.trackVariable(payload);
  }

  /**
   * Creates the baseline header config that uses country lottie animation
   * for both desktop and mobile slots.
   *
   * @param country Supported country code.
   * @returns Default header config prior to country/experiment overrides.
   */
  private createDefaultHeaderConfig(
    country: CountryCodeEnum
  ): LandingHeaderConfig {
    const animationPath = this.getHeaderAnimation(country);

    return {
      topGraphicPath: LANDING_HEADER_TOP_GRAPHIC_PATH,
      mobileBackgroundPath: LANDING_HEADER_MOBILE_BACKGROUND_PATH,
      desktopMedia: {
        type: LandingHeaderMediaType.lottie,
        path: animationPath,
        alt: "Header animation",
      },
      mobileMedia: {
        type: LandingHeaderMediaType.lottie,
        path: animationPath,
        alt: "Header animation",
      },
    };
  }

  /**
   * Central hook for GrowthBook/header experiments to override resolved header
   * config without changing component code.
   *
   * @param country Supported country code.
   * @param config Resolved header config after country overrides.
   * @returns Final header config used by the UI.
   */
  private applyHeaderExperimentOverrides(
    _country: CountryCodeEnum,
    config: LandingHeaderConfig
  ): LandingHeaderConfig {
    return config;
  }

  /**
   * Extracts UTM keys from a search string.
   *
   * @param search Query string in `?key=value` format.
   * @returns UTM key/value map containing only non-empty values.
   */
  private extractUtmsFromSearch(search: string): Record<string, string> {
    const searchParams = new URLSearchParams(search);
    const utms: Record<string, string> = {};

    LANDING_UTM_KEYS.forEach((key) => {
      const value = searchParams.get(key)?.trim();
      if (value) {
        utms[key] = value;
      }
    });

    return utms;
  }
}
